<script>
import { ref, onBeforeUnmount, onBeforeMount, computed } from "vue";
import { useStore } from "vuex";
import { verifyOTP, resendOTP } from "@/api/auth";
import { buildFormData } from "../../utils/param-builder";
import { getProfile } from "../../api/company";
import { useBranding } from "@/utils/branding";

export default {
  data() {
    const { brandLogo, brandName } = useBranding();
    return {
      otpDigits: ["", "", "", "", "", ""],
      errors: {},
      generalError: "",
      countdown: 30,
      canResend: false,
      countdownInterval: null,
      brandLogo,
      brandName,
    };
  },
  methods: {
    startCountdown() {
      this.canResend = false;
      this.countdown = 30;
      this.countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.canResend = true;
          clearInterval(this.countdownInterval);
        }
      }, 1000);
    },
    async resendOTP() {
      if (!this.canResend) return;

      try {
        const body = {
          email: this.emailFromStore,
        };
        const formData = buildFormData(body);
        const response = await resendOTP(formData);

        if (response.status === "success") {
          this.$swal("Success", "OTP has been resent to your email", "success");
          this.startCountdown();
        } else {
          this.$swal(
            "Error",
            response.message || "Failed to resend OTP. Please try again.",
            "error"
          );
        }
      } catch (error) {
        if (error.response) {
          if (error.response.status === 422) {
            const errorMessage = error.response.data.message;
            this.$swal("Error", errorMessage, "error");
          } else {
            this.$swal(
              "Error",
              error.response.data.message ||
                "Failed to resend OTP. Please try again.",
              "error"
            );
          }
        } else {
          this.$swal(
            "Error",
            "An unexpected error occurred. Please try again.",
            "error"
          );
        }
      }
    },
    async handleVerifyOTP() {
      const otp = this.otpDigits.join("");
      const body = {
        email: this.emailFromStore,
        otp: otp,
      };
      const formData = buildFormData(body);
      try {
        const response = await verifyOTP(formData);
        if (response.status === "success") {
          await this.$store.dispatch("auth/setUserToken", response.data.token);
          const profile = await getProfile();
          if (profile.status === "success") {
            this.$store.commit("auth/setProfile", profile.data.merchant);
            localStorage.setItem("merchantId", profile.data.merchant.id);
            localStorage.setItem("role", profile.data.merchant.role.roles_name);
            localStorage.setItem(
              "merchantName",
              profile.data.merchant.company_name
            );
            localStorage.setItem("merchantEmail", profile.data.merchant.email);
            localStorage.setItem("merchant_user_email", profile.data.merchant.merchant_user.email);
            localStorage.setItem(
              "merchantKyb",
              profile.data.merchant.status_kyb
            );
            localStorage.setItem("merchantAppId", profile.data.merchant.app_id);
            localStorage.setItem(
              "merchantAppSecret",
              profile.data.merchant.app_secret
            );
            localStorage.setItem(
              "merchantStatusKyb",
              profile.data.merchant.status_kyb
            );

            // Ensure default environment is sandbox for new/first-time login users
            // Also ensure users with non-approved KYB status cannot have production environment
            const currentEnvironment = localStorage.getItem("environment");
            const kybStatus = profile.data.merchant.status_kyb;

            if (
              !currentEnvironment ||
              (currentEnvironment === "production" && kybStatus !== "approved")
            ) {
              this.$store.dispatch("changeEnvironment", "sandbox");
            } else if (
              currentEnvironment === "production" &&
              kybStatus === "approved"
            ) {
              this.$store.dispatch("changeEnvironment", "production");
            }

            this.$router.push("/home");
          } else {
            this.$swal(
              "Error",
              profile.message || "Failed to fetch profile",
              "error"
            );
            this.$router.push("/signin");
          }
        } else {
          this.$swal(
            "Error",
            response.message || "otp invalid or expired",
            "error"
          );
        }
      } catch (error) {
        if (error.response) {
          if (error.response.status === 422) {
            const erroMassage = error.response.data.message;
            this.$swal("Error", erroMassage, "error");
          }
        } else {
          this.$swal(
            "Error",
            "An unexpected error occurred. Please try again.",
            "error"
          );
        }
      }
    },
    handlePaste(event) {
      // Prevent the default paste behavior
      event.preventDefault();

      // Get the pasted content
      const pastedText = (event.clipboardData || window.clipboardData)
        .getData("text")
        .trim();

      // Check if the pasted content contains numbers
      const numbers = pastedText.replace(/\D/g, "");

      // Take only first 6 digits if available
      const sixDigits = numbers.slice(0, 6);

      if (sixDigits.length > 0) {
        // Fill in as many digits as we have, up to 6
        for (let i = 0; i < sixDigits.length && i < 6; i++) {
          this.otpDigits[i] = sixDigits[i];
        }

        // Focus the next empty input if we didn't fill all
        if (sixDigits.length < 6) {
          const nextIndex = sixDigits.length;
          if (this.$refs["otp-" + nextIndex]) {
            this.$refs["otp-" + nextIndex][0].focus();
          }
        }
      }
    },
    handleInput(index, event) {
      const input = event.target;
      const value = input.value;

      // Only allow numeric input
      if (value.length > 0) {
        if (/^[0-9]$/.test(value)) {
          this.otpDigits[index] = value;
          if (index < 5) {
            this.$refs["otp-" + (index + 1)][0].focus();
          }
        } else {
          // Clear invalid input
          this.otpDigits[index] = "";
          input.value = "";
        }
      }
    },
    handleKeyDown(index, event) {
      if (
        event.key === "Backspace" &&
        this.otpDigits[index] === "" &&
        index > 0
      ) {
        this.$refs["otp-" + (index - 1)][0].focus();
      }
    },
  },
  mounted() {
    this.startCountdown();
  },
  beforeUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  },
  setup() {
    const store = useStore();
    const emailFromStore = ref(
      store.state.auth.email || localStorage.getItem("merchant_email")
    ); // Restore email
    const maskedEmail = computed(() => {
      const [localPart, domain] = emailFromStore.value.split("@");
      const maskedLocal = localPart[0] + "***";
      const [domainName, domainExt] = domain.split(".");
      const maskedDomain = domainName[0] + "**" + domainName.slice(-1);
      return `${maskedLocal}@${maskedDomain}.${domainExt}`;
    });

    const body = document.getElementsByTagName("body")[0];

    onBeforeMount(() => {
      // Restore token from localStorage if available
      const storedToken = localStorage.getItem("auth_token");
      if (storedToken) {
        store.commit("auth/setToken", storedToken); // Restore token to Vuex store
      }
      store.state.hideConfigButton = true;
      store.state.showNavbar = false;
      store.state.showSidenav = false;
      store.state.showFooter = false;
      body.classList.remove("bg-gray-100");
    });

    onBeforeUnmount(() => {
      store.state.hideConfigButton = false;
      store.state.showNavbar = true;
      store.state.showSidenav = true;
      store.state.showFooter = true;
      body.classList.add("bg-gray-100");
    });

    return {
      email: maskedEmail,
      emailFromStore,
    };
  },
  watch: {
    otpDigits: {
      handler(newVal) {
        if (newVal.every((digit) => digit !== "")) {
          this.handleVerifyOTP();
        }
      },
      deep: true,
    },
  },
};
</script>

<template>
  <div class="login-page">
    <!-- Pattern images -->
    <div class="pattern-left">
      <img src="@/assets/img/left-pattern.png" alt="Left Pattern" />
    </div>
    <div class="pattern-right">
      <img src="@/assets/img/right-pattern.png" alt="Right Pattern" />
    </div>

    <div class="container">
      <div class="row justify-content-center align-items-center min-vh-100">
        <div class="col-md-5">
          <div class="card card-custom border-0">
            <div class="card-body p-5">
              <div class="text-center mb-4">
                <!-- Brand logo -->
                <div class="d-inline-flex align-items-center text-success mb-3">
                  <img
                    :src="brandLogo"
                    :alt="brandName"
                    class="mb-3"
                    width="155"
                  />
                </div>

                <h3 class="fw-bold text-title">Enter your OTP</h3>
                <p class="text-muted-custom">
                  We have sent a 6 digit OTP to your email at<br />
                  {{ email }}
                </p>
              </div>

              <!-- OTP input fields -->
              <div class="d-flex justify-content-between mb-4 px-3">
                <input
                  v-for="(digit, index) in otpDigits"
                  :key="index"
                  type="text"
                  pattern="[0-9]*"
                  inputmode="numeric"
                  class="form-control text-center otp-input"
                  maxlength="1"
                  v-model="otpDigits[index]"
                  @input="handleInput(index, $event)"
                  @keydown="handleKeyDown(index, $event)"
                  :ref="'otp-' + index"
                  @paste="handlePaste"
                />
              </div>

              <!-- Resend OTP Section -->
              <div class="text-center mb-4">
                <p class="text-muted-custom mb-2">
                  Didn't receive an OTP?
                  <span v-if="canResend">
                    <button
                      @click="resendOTP"
                      class="btn-link text-success-custom resend-link"
                      type="button"
                    >
                      Resend
                    </button>
                  </span>
                  <span v-else class="text-muted-countdown">
                    Resend ({{
                      String(Math.floor(countdown / 60)).padStart(2, "0")
                    }}:{{ String(countdown % 60).padStart(2, "0") }})
                  </span>
                </p>
              </div>
            </div>

            <!-- Footer inside card -->
            <div class="card-footer bg-white border-0 text-center py-3">
              <small class="text-muted">
                © PT Anugrah Multi Remittance 2025 |
                <a href="#" class="text-decoration-none text-muted"
                  >Term & Conditions</a
                >
                |
                <a href="#" class="text-decoration-none text-muted"
                  >Privacy Policy</a
                >
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-page {
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #ffffff 65%,
    #e8f5e8 65%,
    #d4f1d4 100%
  );
  position: relative;
  overflow: hidden;
}

.login-page::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40vh;
  background: linear-gradient(135deg, #e8f5e8 0%, #d4f1d4 50%, #c8f0c8 100%);
  z-index: 0;
}

/* Pattern Images */
.pattern-left {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.pattern-left img {
  width: auto;
  height: 450px;
  object-fit: contain;
}

.pattern-right {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
}

.pattern-right img {
  width: auto;
  height: 450px;
  object-fit: contain;
}

.container {
  position: relative;
  z-index: 1;
}

.card-custom {
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  z-index: 2;
}

.shadow-lg {
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.text-title {
  font-family: "IBM Plex Sans Devanagari";
  font-weight: 600;
  font-size: 32px;
  line-height: 40px;
  color: #1f2937;
  text-align: center;
}

.text-muted-custom {
  color: #6b7280;
  font-size: 16px;
  line-height: 1.5;
}

.text-success-custom {
  color: #16a34a !important;
  font-weight: 600;
}

.resend-link {
  background: none;
  border: none;
  padding: 0;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
}

.resend-link:hover {
  color: #15803d !important;
  text-decoration: underline;
}

.text-muted-countdown {
  color: #9ca3af;
  font-weight: 500;
}

.otp-input {
  width: 45px !important;
  height: 45px !important;
  font-size: 18px !important;
  font-weight: 600;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  background: #ffffff;
}

.otp-input:focus {
  border-color: #22c55e !important;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1) !important;
  outline: none;
}

.card-footer {
  border-radius: 0 0 1rem 1rem;
}

/* Animation for card entrance */
.card-custom {
  animation: slideUp 0.8s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .login-page::before {
    height: 30vh;
  }

  .pattern-left img,
  .pattern-right img {
    height: 400px;
  }

  .text-title {
    font-size: 24px;
    line-height: 32px;
  }

  .card-body {
    padding: 2rem !important;
  }

  .otp-input {
    width: 40px !important;
    height: 40px !important;
    font-size: 16px !important;
  }
}

@media (max-width: 576px) {
  .pattern-left img,
  .pattern-right img {
    height: 150px;
  }

  .otp-input {
    width: 35px !important;
    height: 35px !important;
    font-size: 14px !important;
  }
}
</style>
